import { atom } from 'jotai';
import { atomFamily } from 'jotai-family';
import { unwrap } from 'jotai/utils';
import { IField, isUniversalKPIField } from '../../../data-models/field2.data-model';
import {
  getOrgPreferenceKey,
  PanelPreferences,
  PreferenceKey,
  PreferenceScope,
} from '../../../data-models/preferences-v3.data-model';

import { kpiConfigAtom, universalAndSystemKPIAtom } from '../../../services/state/KPIConfigState';
import { SelectItem } from '../../../types';
import { preferenceAtom } from '../../../util/preferences-service-v3';

export interface IKpiSelectItem extends SelectItem {
  id: number;
  entityField: string;
}

export const availableKpiChartsAtom = atom((get) => {
  const kpiConfig = get(unwrap(kpiConfigAtom));
  return (
    kpiConfig?.filter(
      (kpi) =>
        kpi.type === 'number' &&
        kpi.formMeta?.renderer?.type !== 'date' &&
        kpi.formMeta?.formatter?.type !== 'date'
    ) ?? []
  );
});

const systemDefaultKpiChartsAtom = atom((get) => {
  return get(availableKpiChartsAtom)
    .filter(isUniversalKPIField)
    .map((kpi) => kpi.entityField);
});

const systemDefaultKpisAtom = atom((get) => {
  return get(universalAndSystemKPIAtom).map((kpi) => kpi.entityField);
});

const _selectedKpisOrgPrefAtom = preferenceAtom<PanelPreferences>({
  preferenceKey: getOrgPreferenceKey(PreferenceKey.companyPerformanceSelectedKPIs, null),
  scope: PreferenceScope.ORGANIZATION,
});

export const selectedKpisOrgPrefAtom = atom(
  (get) => {
    return get(_selectedKpisOrgPrefAtom) ?? { selectedFields: get(systemDefaultKpisAtom) };
  },
  (get, set, newValue: PanelPreferences) => {
    set(_selectedKpisOrgPrefAtom, {
      ...get(_selectedKpisOrgPrefAtom),
      ...newValue,
    });
  }
);

const _selectedKpisByCompanyPrefAtom = atomFamily((companyId: number) => {
  return preferenceAtom<PanelPreferences>([
    {
      preferenceKey: getOrgPreferenceKey(PreferenceKey.companyPerformanceSelectedKPIs, companyId),
      scope: PreferenceScope.ORGANIZATION,
    },
  ]);
});

export const selectedKpisByCompanyPrefAtom = atomFamily((companyId: number) =>
  atom(
    (get) => {
      return get(_selectedKpisByCompanyPrefAtom(companyId)) ?? get(selectedKpisOrgPrefAtom);
    },
    async (get, set, newValue: PanelPreferences) => {
      set(_selectedKpisByCompanyPrefAtom(companyId), {
        ...get(_selectedKpisByCompanyPrefAtom(companyId)),
        ...newValue,
      });
    }
  )
);

const _selectedKPIChartsOrgPrefAtom = preferenceAtom<PanelPreferences>({
  preferenceKey: getOrgPreferenceKey(PreferenceKey.companyPerformanceSelectedKPICharts, null),
  scope: PreferenceScope.ORGANIZATION,
});

export const selectedKPIChartsOrgPrefAtom = atom(
  (get) => {
    return get(_selectedKPIChartsOrgPrefAtom) ?? { selectedFields: get(systemDefaultKpiChartsAtom) };
  },
  (get, set, newValue: PanelPreferences) => {
    set(_selectedKPIChartsOrgPrefAtom, {
      ...get(_selectedKPIChartsOrgPrefAtom),
      ...newValue,
    });
  }
);

const _selectedKPIChartsByCompanyPrefAtom = atomFamily((companyId: number) =>
  preferenceAtom<PanelPreferences>([
    {
      preferenceKey: getOrgPreferenceKey(PreferenceKey.companyPerformanceSelectedKPICharts, companyId),
      scope: PreferenceScope.ORGANIZATION,
    },
  ])
);

export const selectedKPIChartsByCompanyPrefAtom = atomFamily((companyId: number) =>
  atom(
    (get) => {
      return get(_selectedKPIChartsByCompanyPrefAtom(companyId)) ?? get(selectedKPIChartsOrgPrefAtom);
    },
    async (get, set, newValue: PanelPreferences) => {
      set(_selectedKPIChartsByCompanyPrefAtom(companyId), {
        ...get(_selectedKPIChartsByCompanyPrefAtom(companyId)),
        ...newValue,
      });
    }
  )
);

export function kpiSelectItemsToPreference(items: IKpiSelectItem[]): PanelPreferences {
  return {
    selectedFields: items.map(({ entityField }) => entityField).filter((v) => v !== null),
  };
}

export function kpiEntitiesToSelectItem(fields: string[], kpisById: Map<string, IField<unknown>>) {
  return fields.reduce((curr, id) => {
    const field = kpisById.get(id);
    if (!field) {
      return curr;
    }
    return [...curr, kpiFieldToSelectItem(field)];
  }, [] as IKpiSelectItem[]);
}

export function kpiFieldToSelectItem(field: IField<unknown>): IKpiSelectItem {
  return {
    id: field.id,
    entityField: field.entityField,
    value: field.displayName,
    group: field.gridMeta?.group,
  };
}

export function kpiKeysToFields(fields: string[], kpisByKey: Map<string, IField<unknown>>) {
  return fields.reduce((curr, id) => {
    const field = kpisByKey.get(id);
    if (!field) {
      return curr;
    }
    return [...curr, field];
  }, [] as IField<unknown>[]);
}
